import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import {
  fetchDataEngine,
  generateAndExecuteBulkMutation,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import Header from "../../../../components/Header";
import { accountsmanagement } from "../../../../config";
import {
  allAccountSubCategories,
  allBanks,
  allModeOfReceipts,
  allPaymentTypes,
} from "../../../../data/Axios/queries";
import { filterAccountSubCategoriesByUseCase } from "../../../../data/finance/accountCategoryRules";

const base_url = accountsmanagement.uri;

const PaymentVoucherForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData, onAction } = props;

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");

  const [accountTypes, setAccountTypes] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [showreferenceNumber, setShowreferenceNumber] = useState(false);

  const handlePaymentModeChange = (event, setFieldValue) => {
    const selectedModeId = event.target.value;
    if (selectedModeId === 5) {
      setShowreferenceNumber(true);
      return;
    } else {
      setShowreferenceNumber(false);
    }
  };

  const getTodayAsString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate() + 1).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const initialValues = {
    id: data?.id || null,
    voucherDate: data?.voucherDate?.split("T")[0] || getTodayAsString(),
    description: data?.description || "",
    paymentModeId: data?.paymentModeId || "",
    accountTypeId: data?.accountTypeId || "",
    paymentAccountId: data?.paymentAccountId || "",
    referenceNumber: data?.referenceNumber || "",
  };

const handleAccountTypeChange = async (event) => {
    const selectedAccountTypeId = event.target.value;
    try {
      const paymentAccounts = await fetchDataEngine(
        'allAccountsByColumnValues',
        'accounts',
        { accountSubcategoryId: selectedAccountTypeId },
        ['id', 'accountNumber', 'accountName'],
        base_url,
        
      );
      if (paymentAccounts !== null) {
        setPaymentAccounts(paymentAccounts);
      }
    } catch (error) {
      console.error("Error fetching payment accounts:", error);
    }
  };


  const validationSchema = yup.object().shape({
    voucherDate: yup.date().required("Voucher date is required"),
    description: yup.string().required("Description is required"),
    paymentModeId: yup.string().required("Payment mode is required"),
    accountTypeId: yup.string().required("  Account type is required"),
    paymentAccountId: yup.string().required(" Payment account type is required"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  async function fetchData() {
    try {
      const paymentMethods = await graphqlQuery(allModeOfReceipts, base_url);
      if (paymentMethods !== null) {
        setPaymentModes(paymentMethods);
      }
      const accounttypes = await graphqlQuery(allAccountSubCategories, base_url);
      const filteredSubCats = filterAccountSubCategoriesByUseCase(
        accounttypes || [],
        "PAYMENT_VOUCHER_DISBURSEMENT"
      );
      setAccountTypes(filteredSubCats);
    } catch (error) {
      console.error("Error fetching court assignments:", error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        ...values,
        voucherDate: `${values.voucherDate}T00:00:00.000Z`,
        accountTypeId: parseInt(values.accountTypeId),
        paymentAccountId: parseInt(values.paymentAccountId),
        paymentModeId: parseInt(values.paymentModeId),
      };

      const mutationName = isEditing ? "updatePaymentVoucher" : "createPaymentVoucher";
      const responseKey = isEditing ? "updatedPaymentVouchers" : "newPaymentVoucher";

      const response = await generateAndExecuteMutation(   
        mutationName,
        payload,
        responseKey,
        base_url
      );

      if (response) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Payment voucher ${isEditing ? "updated" : "created"} successfully.`,
        });
        if (onAction) {
          onAction();
        } else if (refetchData) {
          refetchData();
        }
        onClose();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Payment Voucher" : "Create Payment Voucher"}
        subtitle={
          isEditing
            ? "Edit existing voucher details"
            : "Fill in the details to create a new payment voucher"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Payment Mode */}
              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Payment Mode *</InputLabel>
                <Select
                  name="paymentModeId"
                  value={values.paymentModeId || ""}
                  onChange={(e) => {
                    handleChange(e);
                    handlePaymentModeChange(e); // Custom handler if needed
                  }}
                  error={touched.paymentModeId && !!errors.paymentModeId}
                >
                  {paymentModes.map((mode) => (
                    <MenuItem key={mode.id} value={mode.id}>
                      {mode.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Account Type */}
              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Account Type *</InputLabel>
                <Select
                  name="accountTypeId"
                  value={values.accountTypeId || ""}
                onChange={(e) => {
                  handleChange(e);
                  handleAccountTypeChange(e); // Custom handler to fetch accounts
                }}
                >
                  {accountTypes
                    .filter((subCat) => {
                      const catId = parseInt(subCat.accountCategoryId);
                      return catId === 1 || catId === 3;
                      })
                      // 👇 THEN MAP
                    .map((subCat) => (
                        <MenuItem key={subCat.id} value={subCat.id}>
                          {subCat.accountCategoryName}
                        </MenuItem>
                  ))}
                </Select>
              </FormControl>

                <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Account *</InputLabel>
                <Select
                  name="paymentAccountId"
                  value={values.paymentAccountId || ""}
                  onChange={handleChange}
                  error={touched.paymentAccountId && !!errors.paymentAccountId}
                >
                  {paymentAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.accountName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Voucher Date */}
              <TextField
                fullWidth
                type="date"
                variant="filled"
                label="Voucher Date *"
                name="voucherDate"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.voucherDate}
                InputLabelProps={{ shrink: true }}
                error={touched.voucherDate && !!errors.voucherDate}
                helperText={touched.voucherDate && errors.voucherDate}
                sx={{ gridColumn: "span 2" }}
              />
              {showreferenceNumber && (
                <TextField
                  fullWidth
                  variant="filled"
                  label="Reference Number"
                  name="referenceNumber"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.referenceNumber}
                  InputLabelProps={{ shrink: true }}
                  error={touched.referenceNumber && !!errors.referenceNumber}
                  helperText={touched.referenceNumber && errors.referenceNumber}
                  sx={{ gridColumn: "span 2" }}
                />
              )}
              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                multiline
                rows={3}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.description}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : isEditing
                  ? "Update"
                  : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PaymentVoucherForm;
